import React, { FC, useMemo, useState, useContext } from "react";
import { useDropzone } from "react-dropzone";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import {
	FileUpload,
	TrainingCourseDuration,
	TrainingCourseRequest,
} from "@/features/training/model/Training/TrainingCourse";
import {
	CustomDeleteButton,
	EditTrainingCourseValidation,
} from "@/features/training/ui";
import { FormAttachment } from "@/shared/forms";
import SATOSEditorField from "@/shared/forms/SATOSEditorField";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { AlertToast } from "@/shared/notifications/AlertToast";
import CloseIcon from "@mui/icons-material/Close";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import { CancelActionButton, SaveActionButton } from "@/shared/ui";
import {
	Autocomplete,
	Box,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { Field, type FieldProps, Form, Formik } from "formik";

import { useFetchGroups } from "@/service/GroupQueries";
import {
	useMutateDeleteTrainingCourse,
	useMutateDeleteTrainingCourseFile,
	useMutateUpdateTrainingCourse,
} from "@/service/TrainingMutations";
import {
	useFetchCourse,
	useFetchCourseFolders,
	useFetchUserSummariesByCurrentCompany,
} from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


/* ──────────────────────────────────────────────────────────────────────────── */
/* Types                                                                        */
/* ──────────────────────────────────────────────────────────────────────────── */

interface EditTrainingCourseProps {
	handleClose: () => void;
	open: boolean;
	courseId: number;
}

interface GroupOption {
	id: number;
	name: string;
}

type FormValues = {
	Name: string;
	Description: string | null;
	Duration: TrainingCourseDuration;
	CourseManagerId: number | "";
	FolderId: number;
	Attachments: FileUpload[];
	NewAttachment: File | null;
	GroupsWithCompetencyRequirement: number[];
};

const ACCEPTED_TYPES = {
	"application/pdf": [".pdf"],
	"application/msword": [".doc"],
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
		".docx",
	],
	"image/jpeg": [".jpg", ".jpeg"],
	"image/png": [".png"],
};
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const toArray = <T,>(maybe: unknown): T[] => {
	if (Array.isArray(maybe)) return maybe as T[];
	if (typeof maybe === "object" && maybe != null) {
		const values = maybe as Record<string, unknown>;
		const upperItems = values["Items"];
		if (Array.isArray(upperItems)) return upperItems as T[];
		const lowerItems = values["items"];
		if (Array.isArray(lowerItems)) return lowerItems as T[];
	}
	return [];
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Dialog shell                                                                 */
/* ──────────────────────────────────────────────────────────────────────────── */

export const EditTrainingCourse: FC<EditTrainingCourseProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	return (
		<Dialog
			onClose={props.handleClose}
			open={props.open}
			fullWidth
			maxWidth="md"
			fullScreen={isMobile}
			aria-labelledby="edit-course-title"
			PaperProps={{
				sx: {
					borderRadius: 2,
					border: `1px solid ${theme.palette.divider}`,
					overflow: "hidden",
					bgcolor: "background.paper",
					display: "flex",
					flexDirection: "column",
					maxHeight: "90vh",
				},
			}}
		>
			<DialogTitle
				id="edit-course-title"
				component="div"
				sx={{
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
					borderBottom: 1,
					borderColor: "divider",
					bgcolor: "background.default",
				}}
			>
				<Typography
					variant="h6"
					component="h2"
					sx={{ fontWeight: 700, color: "text.primary", pr: 5 }}
				>
					<Translate String="Training.EditCourse.Title">Edit Course</Translate>
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 0.5, display: "block" }}
				>
					<Translate String="Training.EditCourse.Subtitle">
						Update core details, ownership, folders, competency requirements,
						and attachments.
					</Translate>
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Ui.Forms.EditTrainingCourse.L176C17.Close", "Close")}
					onClick={props.handleClose}
					size="small"
					sx={{ position: "absolute", right: 8, top: 8, ...closeButtonSx }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent
				sx={{
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
					bgcolor: "background.paper",
					flex: 1,
					overflow: "auto",
				}}
			>
				<EditTrainingCourseForm {...props} />
			</DialogContent>
		</Dialog>
	);
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Section header                                                               */
/* ──────────────────────────────────────────────────────────────────────────── */

const SectionHeader: FC<{
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	icon?: React.ReactNode;
}> = ({ title, subtitle, icon }) => (
	<Box sx={{ mb: 1.5 }}>
		<Stack direction="row" alignItems="center" gap={1}>
			{icon}
			<Typography variant="subtitle1" fontWeight={700}>
				{title}
			</Typography>
		</Stack>
		{subtitle && (
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{ display: "block", mt: 0.25 }}
			>
				{subtitle}
			</Typography>
		)}
	</Box>
);

/* ──────────────────────────────────────────────────────────────────────────── */
/* Form                                                                         */
/* ──────────────────────────────────────────────────────────────────────────── */

const EditTrainingCourseForm: FC<EditTrainingCourseProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const { isLoading: isLoadingParticipants, data: trainerData } =
		useFetchUserSummariesByCurrentCompany();
	const trainerOptions = toArray<{ Id: number; Name: string }>(trainerData);

	const { isLoading: isLoadingFolders, data: foldersRaw } =
		useFetchCourseFolders();
	const folders = toArray<{ Id: number; Name: string }>(foldersRaw);

	const { isLoading: isLoadingCourse, data: course } = useFetchCourse(
		props.courseId,
	);

	const { isLoading: isLoadingGroups, data: groupsRaw } = useFetchGroups();
	const groups = toArray<{
		Id: number;
		Name: string;
		GroupModules?: { Competence?: boolean };
	}>(groupsRaw);

	const updateTrainingCourse = useMutateUpdateTrainingCourse();
	const deleteTrainingCourse = useMutateDeleteTrainingCourse();
	const deleteTrainingCourseFile = useMutateDeleteTrainingCourseFile();
	const [isDeleting, setIsDeleting] = useState(false);

	const competenceGroups: GroupOption[] = useMemo(
		() =>
			groups
				.filter((x) => !!x.GroupModules?.Competence)
				.map((x) => ({ id: x.Id, name: x.Name ?? "" })),
		[groups],
	);

	const handleUpdate = async (formValues: FormValues) => {
		const folderId =
			formValues.FolderId !== 0 ? Number(formValues.FolderId) : 0;
		const req: TrainingCourseRequest = {
			...formValues,
			CourseManagerId:
				formValues.CourseManagerId === ""
					? 0
					: Number(formValues.CourseManagerId),
			FolderId: folderId,
			GroupsWithCompetencyRequirement: (
				formValues.GroupsWithCompetencyRequirement ?? []
			).map((v: number | string) => Number(v)),
		};

		try {
			await updateTrainingCourse({ request: req, courseId: props.courseId });
			props.handleClose();
			void AlertToast.success();
		} catch (error) {
			console.error(error);
			props.handleClose();
			void AlertToast.failed();
		}
	};

	const handleDeleteFile = async (fileId: number) => {
		try {
			await deleteTrainingCourseFile({ courseId: props.courseId, fileId });
			void AlertToast.success();
		} catch (error) {
			console.error(error);
			void AlertToast.failed();
		}
	};

	const handleDelete = async () => {
		if (isDeleting) {
			return;
		}

		try {
			setIsDeleting(true);
			props.handleClose();
			const confirmation = await AlertRequest.delete();
			if (!confirmation.isConfirmed) return;
			await deleteTrainingCourse(props.courseId);
			AlertRequest.deleteConfirmed(course.Name);
		} catch (error) {
			console.error(error);
			void AlertRequest.failed();
		} finally {
			setIsDeleting(false);
		}
	};

	if (
		isLoadingCourse ||
		isLoadingParticipants ||
		isLoadingFolders ||
		isLoadingGroups ||
		!course
	) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<LinearProgress />
			</Box>
		);
	}

	const firstTrainerId = trainerOptions[0]?.Id ?? 0;
	const firstFolderId = folders[0]?.Id ?? 0;

	return (
		<Formik<FormValues>
			enableReinitialize
			initialValues={{
				Name: course.Name ?? "",
				Description: course.Description ?? "",
				Duration: course.Duration ?? TrainingCourseDuration.OneYear,
				CourseManagerId: course.CourseManager?.Id ?? firstTrainerId,
				FolderId: course.FolderId ?? firstFolderId,
				Attachments: course.Attachments ?? [],
				NewAttachment: null as File | null,
				GroupsWithCompetencyRequirement: (
					course.GroupsWithCompetencyRequirement ?? []
				).map((x: { Id: number }) => x.Id),
			}}
			onSubmit={handleUpdate}
			validationSchema={() => EditTrainingCourseValidation(trainerOptions)}
		>
			{({ values, setFieldValue, isSubmitting }) => {
				const selectedGroupIds = Array.isArray(
					values.GroupsWithCompetencyRequirement,
				)
					? values.GroupsWithCompetencyRequirement
					: [];
				const selectedGroups = competenceGroups.filter((g) =>
					selectedGroupIds.includes(g.id),
				);

				const { getRootProps, getInputProps, isDragActive } = useDropzone({
					onDrop: (acceptedFiles: File[]) => {
						const file = acceptedFiles[0];
						if (file) void setFieldValue("NewAttachment", file);
					},
					accept: ACCEPTED_TYPES,
					maxSize: MAX_FILE_SIZE,
					maxFiles: 1,
					multiple: false,
					onDropRejected: (rejections) => {
						const err = rejections[0]?.errors?.[0];
						if (err?.code === "file-too-large") {
							void AlertToast.failed("File exceeds 5 MB limit");
						} else if (err?.code === "file-invalid-type") {
							void AlertToast.failed(
								"Invalid file type. Use PDF, DOC, JPG or PNG.",
							);
						}
					},
				});

				return (
					<Form>
						<Stack spacing={3}>
							{/* 1. Course details */}
							<Paper
								variant="outlined"
								sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2 }}
							>
								<SectionHeader
									title={
										<Translate String="Training.EditCourse.Section.Details">
											Details
										</Translate>
									}
									subtitle={
										<Translate String="Training.EditCourse.Section.Details.Desc">
											Name and describe the course.
										</Translate>
									}
								/>
								<Stack spacing={2}>
									<Field name="Name">
										{({ field, meta }: FieldProps<string, FormValues>) => (
											<TextField
												{...field}
													label={
														<Translate String="Training.EditCourse.Name">
															Name
														</Translate>
													}
													placeholder={GetLanguageValueAsString(
														translation,
														"Training.EditCourse.NamePlaceholder",
														"e.g. Electrical Safety, Level 1",
													)}
												fullWidth
												size="small"
												required
												error={meta.touched && Boolean(meta.error)}
												helperText={meta.touched && meta.error}
												sx={{
													"& .MuiOutlinedInput-root": {
														borderRadius: 2,
														bgcolor: "background.paper",
													},
												}}
											/>
										)}
									</Field>
										<SATOSEditorField
											id="Description"
											name="Description"
											label={GetLanguageValueAsString(
												translation,
												"Common.Description",
												"Description",
											)}
										style={{ minWidth: "100%" }}
										imageUploadDisabled={true}
									/>
								</Stack>
							</Paper>

							{/* 2. Duration & validity */}
							<Paper
								variant="outlined"
								sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2 }}
							>
								<SectionHeader
									icon={
										<ScheduleIcon
											fontSize="small"
											sx={{ color: "text.secondary" }}
										/>
									}
									title={
										<Translate String="Training.EditCourse.Section.Duration">
											Duration
										</Translate>
									}
									subtitle={
										<Translate String="Training.EditCourse.Section.Duration.Desc">
											How long the competency stays valid.
										</Translate>
									}
								/>
								<Stack spacing={2}>
									<Field name="Duration">
										{({
											field,
											form,
										}: FieldProps<TrainingCourseDuration, FormValues>) => (
											<FormControl fullWidth size="small">
												<InputLabel>
													<Translate String="Training.EditCourse.Duration">
														Duration
													</Translate>
												</InputLabel>
												<Select
													{...field}
													label={
														<Translate String="Training.EditCourse.Duration">
															Duration
														</Translate>
													}
													value={field.value ?? ""}
													onChange={(e) => {
														void form.setFieldValue(
															"Duration",
															e.target.value as TrainingCourseDuration,
														);
													}}
													sx={{
														borderRadius: 2,
														bgcolor: "background.paper",
													}}
												>
													{Object.values(TrainingCourseDuration).map(
														(duration) => (
															<MenuItem key={duration} value={duration}>
																{duration.replace(/([A-Z])/g, " $1").trim()}
															</MenuItem>
														),
													)}
												</Select>
											</FormControl>
										)}
									</Field>
									{values.Duration === TrainingCourseDuration.Custom && (
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ display: "block" }}
										>
											<Translate String="Training.EditCourse.Duration.CustomHint">
												When Custom: set the expiry date per session when
												creating or editing sessions.
											</Translate>
										</Typography>
									)}
								</Stack>
							</Paper>

							{/* 3. Ownership & folder */}
							<Paper
								variant="outlined"
								sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2 }}
							>
								<SectionHeader
									icon={
										<SupervisorAccountIcon
											fontSize="small"
											sx={{ color: "text.secondary" }}
										/>
									}
									title={
										<Translate String="Training.EditCourse.Section.Ownership">
											Ownership & Folder
										</Translate>
									}
									subtitle={
										<Translate String="Training.EditCourse.Section.Ownership.Desc">
											Select the course manager and where the course sits.
										</Translate>
									}
								/>
								<Stack spacing={2}>
									<Field name="CourseManagerId">
										{({
											field,
											meta,
											form,
										}: FieldProps<number | "", FormValues>) => (
											<FormControl
												fullWidth
												size="small"
												error={meta.touched && Boolean(meta.error)}
												sx={{
													"& .MuiOutlinedInput-root": {
														borderRadius: 2,
														bgcolor: "background.paper",
													},
												}}
											>
												<InputLabel>
													<Translate String="Training.EditCourse.Manager">
														Manager
													</Translate>
												</InputLabel>
												<Select
													{...field}
													label={
														<Translate String="Training.EditCourse.Manager">
															Manager
														</Translate>
													}
													value={field.value ?? ""}
													onChange={(e) => {
														void form.setFieldValue(
															"CourseManagerId",
															e.target.value === ""
																? ""
																: Number(e.target.value),
														);
													}}
													displayEmpty
												>
													<MenuItem value="">
														<em>
															<Translate String="Training.EditCourse.SelectManager">
																Select manager...
															</Translate>
														</em>
													</MenuItem>
													{trainerOptions.map((x) => (
														<MenuItem key={x.Id} value={x.Id}>
															{x.Name}
														</MenuItem>
													))}
												</Select>
												{meta.touched && meta.error && (
													<FormHelperText>{meta.error}</FormHelperText>
												)}
											</FormControl>
										)}
									</Field>
									<Field name="FolderId">
										{({ field, form }: FieldProps<number, FormValues>) => (
											<FormControl
												fullWidth
												size="small"
												sx={{
													"& .MuiOutlinedInput-root": {
														borderRadius: 2,
														bgcolor: "background.paper",
													},
												}}
											>
												<InputLabel>
													<Translate String="Training.EditCourse.Folder">
														Folder
													</Translate>
												</InputLabel>
												<Select
													{...field}
													label={
														<Translate String="Training.EditCourse.Folder">
															Folder
														</Translate>
													}
													value={field.value ?? ""}
													onChange={(e) => {
														const v = e.target.value;
														void form.setFieldValue(
															"FolderId",
															v === "" || v === "General" ? 0 : Number(v),
														);
													}}
													displayEmpty
												>
													<MenuItem value={0}>
														<em>
															<Translate String="Common.General">
																General
															</Translate>
														</em>
													</MenuItem>
													{folders.map((x) => (
														<MenuItem key={x.Id} value={x.Id}>
															{x.Name}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										)}
									</Field>
								</Stack>
							</Paper>

							{/* 4. Competency requirements */}
							<Paper
								variant="outlined"
								sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2 }}
							>
								<SectionHeader
									title={
										<>
											<Translate String="Training.EditCourse.Section.Competency">
												Competency requirements
											</Translate>
											{selectedGroupIds.length > 0 && (
												<Typography
													component="span"
													variant="body2"
													color="primary"
													sx={{ ml: 1, fontWeight: 600 }}
												>
													({selectedGroupIds.length}{" "}
													<Translate String="Common.Selected">selected</Translate>)
												</Typography>
											)}
										</>
									}
									subtitle={
										<Translate String="Training.EditCourse.Section.Competency.Desc">
											Which groups must hold this competency.
										</Translate>
									}
								/>
								<Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
									<ProcedureButton
										size="small"
										variant="outlined"
										onClick={() => {
											void setFieldValue(
												"GroupsWithCompetencyRequirement",
												competenceGroups.map((g) => g.id),
											);
										}}
										sx={{ whiteSpace: "nowrap" }}
									>
										<Translate String="Common.SelectAll">Select all</Translate>
									</ProcedureButton>
									<ProcedureButton
										size="small"
										variant="outlined"
										intent="warning"
										onClick={() => {
											void setFieldValue("GroupsWithCompetencyRequirement", []);
										}}
										sx={{ whiteSpace: "nowrap" }}
									>
										<Translate String="Common.ClearAll">Clear all</Translate>
									</ProcedureButton>
								</Stack>
								<Autocomplete
									multiple
									options={competenceGroups}
									getOptionLabel={(opt) =>
										typeof opt === "string" ? opt : opt.name
									}
									value={selectedGroups}
									onChange={(_, newVal) => {
										void setFieldValue(
											"GroupsWithCompetencyRequirement",
											newVal.map((g) => g.id),
										);
									}}
									filterSelectedOptions
									renderInput={(params) => (
										<TextField
											{...params}
											placeholder={GetLanguageValueAsString(
												translation,
												"Training.EditCourse.SearchOrSelectGroups",
												"Search or select groups...",
											)}
											size="small"
											sx={{
												"& .MuiOutlinedInput-root": {
													bgcolor: "background.paper",
													borderRadius: 2,
												},
											}}
										/>
									)}
									renderOption={(props, option, { selected }) => (
										<li
											{...props}
											key={option.id}
											style={{
												borderBottom: "1px solid",
												borderColor: "divider",
											}}
										>
											<Checkbox
												checked={selected}
												size="small"
												sx={{ mr: 1 }}
											/>
											{option.name}
										</li>
									)}
								/>
							</Paper>

							{/* 5. Attachments */}
							<Paper
								variant="outlined"
								sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2 }}
							>
								<SectionHeader
									icon={
										<FolderOpenIcon
											fontSize="small"
											sx={{ color: "text.secondary" }}
										/>
									}
									title={
										<Translate String="Training.EditCourse.Attachments">
											Attachments
										</Translate>
									}
									subtitle={
										<Translate String="Training.EditCourse.Attachments.Desc">
											Upload reference files or supporting material.
										</Translate>
									}
								/>
								<Stack spacing={2}>
									{(course.Attachments ?? []).length > 0 && (
										<Stack direction="row" flexWrap="wrap" gap={1}>
											{(course.Attachments ?? []).map((attachment) => (
												<FormAttachment
													key={attachment.Id}
													id={attachment.Id}
													filename={attachment.Filename}
													handleDelete={handleDeleteFile}
												/>
											))}
										</Stack>
									)}
									{(course.Attachments ?? []).length === 0 &&
										!values.NewAttachment && (
											<Typography variant="body2" color="text.secondary">
												<Translate String="Training.EditCourse.Attachments.None">
													No attachments yet.
												</Translate>
											</Typography>
										)}
									<Box
										{...getRootProps()}
										sx={{
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											border: "2px dashed",
											borderColor: isDragActive ? "primary.main" : "divider",
											borderRadius: 2,
											py: 3,
											px: 2,
											textAlign: "center",
											cursor: "pointer",
											bgcolor: isDragActive
												? "action.hover"
												: "background.default",
											color: "text.secondary",
											transition: "border-color 0.2s, background-color 0.2s",
											"&:hover": {
												borderColor: "primary.main",
												bgcolor: "action.hover",
											},
										}}
									>
										<input {...getInputProps()} id="edit-course-attachment" />
										{values.NewAttachment ? (
											<Stack
												direction="row"
												alignItems="center"
												spacing={2}
												sx={{
													p: 2,
													borderRadius: 1,
													bgcolor: "background.paper",
													width: "100%",
													maxWidth: 400,
													border: "1px solid",
													borderColor: "divider",
												}}
											>
												<InsertDriveFileOutlinedIcon
													sx={{ color: "text.secondary", fontSize: 32 }}
												/>
												<Box sx={{ flex: 1, minWidth: 0 }}>
													<Typography variant="body2" fontWeight={600} noWrap>
														{values.NewAttachment.name}
													</Typography>
												</Box>
												<IconButton
													onClick={(e) => {
														e.stopPropagation();
														void setFieldValue("NewAttachment", null);
													}}
													color="error"
													size="small"
													aria-label={GetLanguageValueAsString(
														translation,
														"Common.RemoveFile",
														"Remove file",
													)}
												>
													<CloseIcon fontSize="small" />
												</IconButton>
											</Stack>
										) : (
												<>
													<Typography variant="body2" sx={{ mb: 0.5 }}>
														<Translate String="Shift.Event.Dropzone.Drag">
															Drag a file here or click to browse
														</Translate>
													</Typography>
												<Typography variant="caption">
													<Translate String="Shift.Event.File.Help">
														PDF, DOC, JPG, PNG · max 5 MB
													</Translate>
												</Typography>
											</>
										)}
									</Box>
								</Stack>
							</Paper>
						</Stack>

						{/* Footer */}
						<DialogActions
							sx={{
								px: 0,
								py: 2,
								mt: 2,
								borderTop: 1,
								borderColor: "divider",
								flexWrap: "wrap",
								justifyContent: "space-between",
								gap: 1.5,
								display: "flex",
							}}
						>
							<CustomDeleteButton
								handleDelete={handleDelete}
								loading={isDeleting}
								disabled={
									(course.Sessions ?? []).length > 0 ||
									isDeleting ||
									isSubmitting
								}
								sx={{ whiteSpace: "nowrap" }}
								textOverride={
									<Translate String="Training.EditCourse.DeleteButton">
										Delete course
									</Translate>
								}
							/>
							<Stack direction="row" spacing={1.5}>
								<CancelActionButton
									onClick={props.handleClose}
									disabled={isDeleting || isSubmitting}
								>
									<Translate String="Procedure.Button.Cancel">Cancel</Translate>
								</CancelActionButton>
								<SaveActionButton
									type="submit"
									loading={isSubmitting}
									loadingText={
										<Translate String="LabDaily.Saving">Saving...</Translate>
									}
									disabled={isDeleting || isSubmitting}
								>
									<Translate String="Training.EditCourse.SaveChanges">
										Save changes
									</Translate>
								</SaveActionButton>
							</Stack>
						</DialogActions>
					</Form>
				);
			}}
		</Formik>
	);
};
